import { useMemo } from 'react';
import { useUser } from '@/queries/useUser';
import { GENERIC_STATUS } from '@/lib/constants';
import { useOnboardingStepStatus } from './useOnboardingStepStatus';
import { useOnboardingSessionStorage } from './useOnboardingSessionStorage';

/**
 * Hook to determine if all onboarding tasks are completed.
 * Checks if all 4 onboarding steps are completed (including skipped steps):
 * 1. Profile image uploaded
 * 2. Social links added
 * 3. Direct deposit setup
 * 4. First product added
 *
 * @returns Object with `allTasksCompleted` boolean indicating if all tasks are done
 */
export function useAllOnboardingTasksCompleted(): { allTasksCompleted: boolean } {
    const { user } = useUser();
    const { isProfileImageUploaded, isSocialLinksAdded, isFirstProductAdded, isDirectDepositAdded } =
        useOnboardingStepStatus();
    const { getAllStepsReady } = useOnboardingSessionStorage();

    const showOnboardingDashboard = useMemo(() => {
        return user?.['onboarding-dashboard']?.status === GENERIC_STATUS.ACTIVE;
    }, [user]);

    const allTasksCompleted = useMemo(() => {
        if (!showOnboardingDashboard) return false;

        const stepsReady = getAllStepsReady();

        // Count completed tasks (including skipped)
        let count = 0;
        if (isProfileImageUploaded || stepsReady.profileImage) count++;
        if (isSocialLinksAdded || stepsReady.socialLinks) count++;
        if (isDirectDepositAdded || stepsReady.depositSetup) count++;
        if (isFirstProductAdded || stepsReady.firstProduct) count++;

        return count === 4;
    }, [
        showOnboardingDashboard,
        isProfileImageUploaded,
        isSocialLinksAdded,
        isDirectDepositAdded,
        isFirstProductAdded,
        getAllStepsReady,
    ]);

    return { allTasksCompleted };
}
